import { createClient, type FunctionRegion } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY environment variables"
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ── Auth helpers ─────────────────────────────────────────────────────────────

export const ADMIN_KEY_HEADER = "x-admin-key";
export const ADMIN_PASSCODE_STORAGE_KEY = "interleague_admin_passcode";

/** Read admin key from localStorage only. */
export const getAdminKey = (): string => {
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(ADMIN_PASSCODE_STORAGE_KEY)?.trim();
    if (stored) return stored;
  }
  return "";
};

// ── Error helpers ─────────────────────────────────────────────────────────────

/** Extracts a message string from a Supabase or generic error object. */
export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message) || fallback;
  }
  return fallback;
};

/** Throws an Error with the Supabase/generic error message, or the fallback. */
export const throwOnError = (error: unknown, fallback: string): never => {
  throw new Error(getErrorMessage(error, fallback));
};

// ── Admin passcode verification ───────────────────────────────────────────────

export type VerifyAdminResult = {
  ok: boolean;
  networkError?: boolean;
  /** True when ADMIN_KEY is not configured on the server (503). */
  notConfigured?: boolean;
};

/**
 * Verify an admin passcode against the `verify-admin` Supabase Edge Function.
 * Returns a structured result instead of throwing so callers can show specific
 * error messages for wrong passcode vs. server misconfiguration vs. network issues.
 */
export const verifyAdminPasscode = async (
  passcode: string,
  signal?: AbortSignal
): Promise<VerifyAdminResult> => {
  try {
    // cast needed until @supabase/supabase-js types expose `signal` on invoke options
    const { error } = await supabase.functions.invoke("verify-admin", {
      body: { passcode },
      signal,
    } as Parameters<typeof supabase.functions.invoke>[1]);

    if (!error) return { ok: true };

    if (typeof error === "object" && "context" in error) {
      const ctx = (error as Record<string, unknown>).context;
      if (ctx instanceof Response) {
        if (ctx.status === 503) return { ok: false, notConfigured: true };
        if (ctx.status === 401) return { ok: false };
        return { ok: false, networkError: true };
      }
    }

    if (error instanceof DOMException && error.name === "AbortError") {
      return { ok: false, networkError: true };
    }

    return { ok: false, networkError: true };
  } catch {
    return { ok: false, networkError: true };
  }
};

// ── Edge Function helper ──────────────────────────────────────────────────────

/**
 * Invoke a Supabase Edge Function.
 *
 * @param name         Edge Function name (e.g. "upload-matches")
 * @param body         JSON body to send
 * @param requireAdmin If true, adds the x-admin-key header automatically
 */
export const invokeEdgeFunction = async <T = unknown>(
  name: string,
  body: unknown,
  requireAdmin = false,
  // Edge Functions run at the caller's nearest edge by default; a function that makes several
  // database queries per call is faster pinned to the database's region.
  options: { region?: FunctionRegion } = {}
): Promise<T> => {
  const headers: Record<string, string> = {};
  if (requireAdmin) {
    headers[ADMIN_KEY_HEADER] = getAdminKey();
  }

  const { data, error } = await supabase.functions.invoke<T>(name, {
    body: body as Record<string, unknown>,
    headers,
    ...(options.region ? { region: options.region } : {}),
  });

  if (error) {
    // FunctionsHttpError carries the raw Response in .context — try to extract
    // the actual error message so users see the real reason, not the generic
    // "Edge Function returned a non-2xx status code".
    if (error && typeof error === "object" && "context" in error) {
      // Try to extract the real error message from the Edge Function response body.
      // If JSON parsing fails, fall through and re-throw the original error below.
      let parsed: { error?: string } | null = null;
      let status: number | null = null;
      try {
        const ctx = (error as Record<string, unknown>).context;
        if (ctx instanceof Response) {
          status = ctx.status;
          parsed = await ctx.clone().json() as { error?: string };
        } else if (ctx && typeof ctx === "object" && "error" in (ctx as object)) {
          parsed = ctx as { error?: string };
        }
      } catch {
        // JSON parse failed — ignore and fall through to throw original error
      }

      // A 401 means the server would not accept this sign-in. An access token can be validly signed
      // and unexpired while its session no longer exists server-side - signing in elsewhere, or
      // signing out, deletes the session and the auth server then answers "session not found". The
      // browser cannot tell locally, so it keeps showing the player as signed in and every action
      // fails. Dropping the dead session locally puts the UI back in its signed-out state, where the
      // normal sign-in door is offered.
      if (status === 401) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* already gone */ }
        throw new Error(parsed?.error ?? "Your sign-in is no longer valid. Please sign in again.");
      }

      if (parsed?.error) throw new Error(parsed.error);
    }
    throw error instanceof Error ? error : new Error(getErrorMessage(error, "Edge Function request failed"));
  }
  return data as T;
};
